
import React, { useMemo, useState } from "react";
import Icon from "./Findcareicons";
import content from "./FindcareContent.json";
import { usePrograms } from "../hooks/useLifeStages";
import { useServices } from "../hooks/useServices";
import { useDoctors } from "../hooks/useDoctors";
import { useNavigate } from "react-router-dom";
import TalkToCareGuideButton from "../components/whatsapp/whatsappIntegration";

// ─────────────────────────────────────────────────────────────────────────────
// THEME TOKENS — identical to your CareJourney.jsx `T` object
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  purple900: "#1A0A3C", purple800: "#2D1466", purple700: "#3B1A8C",
  purple600: "#4C24A8", purple500: "#5B2EBF", purpleBtn: "#3D1A8A",
  purpleLight: "#EDE9FB", purpleBorder: "#6B48D3", purpleTag: "#E8E3FA",
  heading: "#1A1033", body: "#374151", sub: "#6B7280", muted: "#9CA3AF",
  border: "#E5E7EB", pageBg: "#FFFFFF", lavenderBg: "#F4F1FD", cardBg: "#FFFFFF",
  infoBg: "#EFF6FF", infoBorder: "#BFDBFE", infoText: "#1E40AF",
  green: "#10B981", amber: "#F59E0B", red: "#EF4444",
};

// ─────────────────────────────────────────────────────────────────────────────
// CATALOGUE LAYER
//
// Screen 1's careNeed choice ("consultation" / "diagnostics" / "programs" /
// "symptoms") decides WHICH already-fetched list Screen 2 and Screen 4 read
// from. This section is the one place that decision gets made — everything
// below it just consumes `items` without caring whether they're services or
// programs.
// ─────────────────────────────────────────────────────────────────────────────

/** A service record's serviceType/categoryName decides which careNeed bucket it belongs to. */
const CARE_NEED_MATCHERS = {
  symptoms: (s) => s.serviceType === "Consultation",
  // consultation: (s) => s.serviceType === "Consultation",
  // diagnostics: (s) =>
  //   s.serviceType === "Diagnostic" ||
  //   /diagnos|test|scan|screening|lab/i.test(`${s.categoryName || ""}`),
  diagnostics: (s) => s.serviceType === "Consultation",


};
const matchesCareNeed = (service, careNeed) =>
  CARE_NEED_MATCHERS[careNeed] ? CARE_NEED_MATCHERS[careNeed](service) : true;

const matchesLifeStage = (item, lifeStageId) => {
  if (!lifeStageId) return true;
  if (!item.lifeStages || item.lifeStages.length === 0) return true; // unrestricted = available to everyone
  return item.lifeStages.some((ls) => ls.id === lifeStageId);
};

const iconForService = (s) => {
  const name = `${s.categoryName || ""} ${s.serviceName || ""}`.toLowerCase();
  if (name.includes("pregnan") || name.includes("obstetric")) return "pregnancy";
  if (name.includes("menopause")) return "lotus";
  if (name.includes("diagnos") || name.includes("lab") || name.includes("scan")) return "flask";
  if (name.includes("endocrin") || name.includes("hormone")) return "stethoscope";
  return "doctorChat";
};

const iconForConsultMode = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("video")) return "video";
  if (n.includes("phone") || n.includes("audio")) return "phone";
  return "clinic";
};

// Falls back to a sensible icon/color when the API life stage object
// doesn't supply one — keyed off the name so it works regardless of casing
// or exact wording ("Reproductive Age" vs "reproductive_age" etc.)
function iconForLifeStage(name = "") {
  const n = name.toLowerCase();
  if (n.includes("adolescent") || n.includes("teen")) return "sprout";
  if (n.includes("reproductive")) return "bloom";
  if (n.includes("pregnan")) return "pregnancy";
  if (n.includes("postpartum")) return "postpartum";
  if (n.includes("menopause")) return "lotus";
  return "person";
}

function badgeColorForLifeStage(name = "") {
  const n = name.toLowerCase();
  if (n.includes("pregnan") || n.includes("postpartum")) return "#EDE4FB";
  return "#FBE7F1";
}
/** All condition/symptom focuses an item (service OR program) can be booked for, deduped. */
const focusesForItem = (item) => {
  const list = [...(item?.conditionFocuses || []), ...(item?.symptomFocuses || [])];
  const seen = new Set();
  return list.filter((f) => {
    if (!f || seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
};

// ── Normalizers: raw API record -> one common card shape ───────────────────
function normalizeService(s) {
  return {
    id: s.id,
    kind: "service",
    icon: iconForService(s),
    label: s.serviceName,
    desc: s.description,
    lifeStages: s.lifeStages || [],
    focuses: focusesForItem(s),
    consultationModes: s.consultationModes || [],
    duration: s.duration,
    raw: s,
  };
}
// Turns a static symptoms-branch JSON option into the same card shape used
// by services/doctors/programs, so RecommendationsScreen can render it with
// zero special-casing beyond picking which item is "best".
function normalizeSymptomOption(opt, reason) {
  return {
    id: opt.id,
    kind: "symptom",
    icon: opt.icon,
    label: opt.label,
    desc: opt.desc,
    lifeStages: [],
    focuses: reason ? [{ id: reason, name: reason }] : [],
    consultationModes: [],
    duration: 30,
    raw: opt,
  };
}
// function normalizeDoctor(d) {
//   return {
//     id: d.providerId || d.id,
//     kind: "doctor",
//     icon: "doctorChat",
//     label: d.name || d.fullName || d.providerName || "Doctor",
//     desc: d.specialization || d.qualification || d.designation || d.about,
//     lifeStages: d.lifeStages || [],
//     focuses: focusesForItem(d),
//     consultationModes: d.consultationModes || d.availableModes || [],
//     duration: d.avgConsultDuration || d.duration,
//     raw: d,
//   };
// }
function iconForSpeciality(name = "") {
  const n = name.toLowerCase();
  if (n.includes("psych")) return "mind";
  if (n.includes("gynae") || n.includes("obstetric")) return "pregnancy";
  if (n.includes("endocrin") || n.includes("hormone")) return "stethoscope";
  if (n.includes("derma")) return "flower";
  return "doctorChat";
}

function normalizeDoctor(d) {
  const speciality = d.speciality || null;
  return {
    id: d.providerId || d.id,
    kind: "doctor",
    icon: iconForSpeciality(speciality?.specialityName),
    label: (d.fullName || d.name || "Doctor").trim(), // trims stray spaces like "Dr. Riya "
    desc: speciality
      ? [speciality.specialityName, speciality.description].filter(Boolean).join(" · ")
      : d.about,
    lifeStages: d.lifeStages || [],
    // A doctor only ever has ONE speciality — expose it as a single-item
    // "focus" so it slots straight into the existing Q2 focus-chip UI
    // without any doctor-specific branching downstream.
    focuses: speciality ? [{ id: speciality.id, name: speciality.specialityName }] : [],
    consultationModes: d.consultationModes || d.availableModes || [],
    duration: d.avgConsultDuration || d.duration,
    raw: d,
  };
}

function normalizeProgram(p) {
  return {
    id: p.id,
    kind: "program",
    icon: "heartPulse",
    // Adjust these two lines once you confirm the real field names your
    // programs endpoint returns — everything downstream is unaffected.
    label: p.name || p.title || p.programName,
    desc: p.description || p.summary || p.programDescription,
    lifeStages: p.lifeStages || [],
    focuses: focusesForItem(p),
    consultationModes: p.consultationModes || [], // most programs won't have per-visit modes — Screen 2/4 handle that being empty
    duration: p.duration,
    raw: p,
  };
}

// One row per care-need bucket: which fetched array to read, an optional
// pre-filter (for splitting one combined services list into consultation vs
// diagnostics), and how to normalize items into the shared card shape.
const CATALOGUE_CONFIG = {
  diagnostics: { source: "services", filter: (s) => matchesCareNeed(s, "diagnostics"), normalize: normalizeService },
  consultation: { source: "doctors", filter: null, normalize: normalizeDoctor },
  symptoms: { source: "services", filter: (s) => matchesCareNeed(s, "symptoms"), normalize: normalizeService },
  programs: { source: "programs", filter: null, normalize: normalizeProgram },
};

/**
 * useCatalogue("programs", { services, servicesLoading, servicesError, programs, programsLoading, programsError })
 *   -> { items: [{id, icon, label, desc, lifeStages, focuses, consultationModes, raw}], loading, error }
 */
function useCatalogue(careNeed, ctx) {
  const config = CATALOGUE_CONFIG[careNeed] || CATALOGUE_CONFIG.diagnostics;
  const rawItems = ctx[config.source] || [];
  const loading = ctx[`${config.source}Loading`] || false;
  const error = ctx[`${config.source}Error`] || null;

  const items = useMemo(() => {
    const filtered = config.filter ? rawItems.filter(config.filter) : rawItems;
    return filtered.map(config.normalize);
  }, [rawItems, config]);

  return { items, loading, error };
}

/** Scores one catalogue item against the selections made so far on Screens 1–3. */
function scoreItem(item, data) {
  const pickedFocusIds = (data.conditionFocuses || []).map((f) => f.id);
  const itemFocusIds = item.focuses.map((f) => f.id);
  let score = 0;
  pickedFocusIds.forEach((id) => { if (itemFocusIds.includes(id)) score += 2; });
  if (matchesLifeStage(item, data.lifeStage)) score += 1;
  if (data.consultType2 && item.consultationModes.some((m) => iconForConsultMode(m.name) === data.consultType2)) {
    score += 1;
  }
  return score;
}

/**
 * Turns a selected service/program's `detail.condition` (or condition/name
 * fallback) into a stable JSON-safe key: "PCOS" -> "pcos",
 * "Heavy Bleeding" -> "heavy_bleeding". Falls back to "default" so a lookup
 * never throws on missing data.
 */
function getConditionKey(itemMeta) {
  const raw =
    itemMeta?.detail?.condition ||
    itemMeta?.condition ||
    itemMeta?.serviceName ||
    itemMeta?.name ||
    "";
  const slug = raw.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return slug || "default";
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
function StepperProgress({ current, total }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <div style={{
            width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700,
            background: i < current ? T.purple700 : i === current ? T.purple700 : "#fff",
            color: i <= current ? "#fff" : T.muted,
            border: i <= current ? "none" : `1.5px solid ${T.border}`,
          }}>
            {i < current ? <Icon name="check" size={12} color="#fff" /> : i + 1}
          </div>
          {i < total - 1 && <div style={{ width: 28, height: 2, background: i < current ? T.purple700 : T.border }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

const BtnBack = ({ onClick }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 8,
    fontSize: 12, fontWeight: 600, border: `1.5px solid ${T.border}`, background: "#fff",
    color: T.body, cursor: "pointer",
  }}>
    <Icon name="chevronLeft" size={14} /> Back
  </button>
);

const BtnContinue = ({ onClick, disabled, label = "Continue", wide = false }) => (
  <button onClick={onClick} disabled={disabled} style={{
    display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 8,
    fontSize: 13, fontWeight: 700, color: "#fff", border: "none",
    background: disabled ? "#C9C2E6" : T.purpleBtn, cursor: disabled ? "not-allowed" : "pointer",
    width: wide ? "100%" : undefined, justifyContent: "center",
  }}>
    {label} <Icon name="chevronRight" size={14} color="#fff" />
  </button>
);

const SelectCard = ({ icon, label, desc, selected, onClick }) => (
  <button onClick={onClick} style={{
    position: "relative", textAlign: "left", borderRadius: 12, padding: "16px 14px",
    border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: selected ? T.purpleLight : "#fff",
    cursor: "pointer", display: "flex", flexDirection: "column", gap: 10, transition: "all 0.15s",
  }}>
    {selected && (
      <div style={{
        position: "absolute", top: 10, right: 10, width: 20, height: 20, borderRadius: "50%",
        background: T.purple700, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon name="check" size={11} color="#fff" strokeWidth={2.4} />
      </div>
    )}
    <div style={{
      width: 40, height: 40, borderRadius: "50%",
      background: selected ? "#fff" : T.purpleLight,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Icon name={icon} size={20} color={T.purple700} />
    </div>
    <div>
      <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, margin: "0 0 2px" }}>{label}</p>
      {desc && <p style={{ fontSize: 11, color: T.sub, margin: 0, lineHeight: 1.4 }}>{desc}</p>}
    </div>
  </button>
);

const BadgeCard = ({ icon, label, desc, badgeColor, selected, onClick }) => (
  <button onClick={onClick} style={{
    position: "relative", textAlign: "center", borderRadius: 12, padding: "20px 14px 16px",
    border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: selected ? T.purpleLight : "#fff",
    cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    transition: "all 0.15s",
  }}>
    {selected && (
      <div style={{
        position: "absolute", top: 10, right: 10, width: 18, height: 18, borderRadius: "50%",
        background: T.purple700, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon name="check" size={10} color="#fff" strokeWidth={2.6} />
      </div>
    )}
    <div style={{
      width: 56, height: 56, borderRadius: "50%",
      background: badgeColor || T.purpleLight,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Icon name={icon} size={26} color={T.purple700} strokeWidth={1.6} />
    </div>
    <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, margin: 0 }}>{label}</p>
    {desc && <p style={{ fontSize: 10.5, color: T.sub, margin: 0, lineHeight: 1.4 }}>{desc}</p>}
  </button>
);

const SelectChip = ({ label, selected, onClick }) => (
  <button onClick={onClick} style={{
    padding: "9px 16px", borderRadius: 8, fontSize: 12, fontWeight: selected ? 700 : 500,
    border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: selected ? T.purpleLight : "#fff",
    color: selected ? T.purple700 : T.body, cursor: "pointer", transition: "all 0.15s",
  }}>
    {label}
  </button>
);

const IconChip = ({ icon, label, selected, onClick }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 10,
    border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: selected ? T.purpleLight : "#fff", cursor: "pointer", textAlign: "left",
  }}>
    <Icon name={icon} size={16} color={selected ? T.purple700 : T.sub} />
    <span style={{ fontSize: 12, fontWeight: selected ? 700 : 500, color: selected ? T.purple700 : T.body }}>{label}</span>
    {selected && <Icon name="check" size={12} color={T.purple700} style={{ marginLeft: "auto" }} />}
  </button>
);

const QuestionBlock = ({ label, helper, children }) => (
  <div style={{ marginBottom: 22 }}>
    <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, margin: "0 0 2px" }}>{label}</p>
    {helper && <p style={{ fontSize: 11, color: T.muted, margin: "0 0 12px" }}>{helper}</p>}
    {!helper && <div style={{ height: 12 }} />}
    {children}
  </div>
);

const FooterBar = ({ left, center, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 0", marginTop: 14, borderTop: `1px solid ${T.border}` }}>
    <div style={{ flex: 1 }}>{left}</div>
    <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>{center}</div>
    <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>{right}</div>
  </div>
);

const SummaryRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{ width: 30, height: 30, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon name={icon} size={15} color={T.purple700} />
    </div>
    <div>
      <p style={{ fontSize: 10, color: T.muted, margin: 0 }}>{label}</p>
      <p style={{ fontSize: 12, fontWeight: 700, color: T.heading, margin: 0 }}>{value}</p>
    </div>
  </div>
);

const InlineState = ({ loading, error, loadingLabel = "Loading...", errorLabel = "Couldn't load this — please try again." }) => {
  if (loading) return <p style={{ fontSize: 12, color: T.muted }}>{loadingLabel}</p>;
  if (error) return <p style={{ fontSize: 12, color: T.red }}>{errorLabel}</p>;
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 1 — Care Need + Life Stage
// (lifeStages now comes in as a prop from the root, so it's only fetched once)
// ─────────────────────────────────────────────────────────────────────────────
export function CareNeedScreen({ data, onChange, onNext, stepIndex, totalSteps, lifeStages = [] }) {
  const c = content.screen1_careNeed;
  const canContinue = data.careNeed && data.lifeStage;

  return (
    <div style={{ display: "flex", gap: 28, 
    // alignItems: "flex-start" 
    alignItems:"center"
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div style={{ maxWidth: 560 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: T.heading, margin: "0 0 6px" }}>{c.title}</h1>
            <p style={{ fontSize: 12, color: T.sub, margin: 0, lineHeight: 1.5 }}>{c.subtitle}</p>
          </div>
          <StepperProgress current={stepIndex} total={totalSteps} />
        </div>

        <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 18 }}>
          <QuestionBlock label={`1. ${c.careNeedQuestion.label}`} helper={c.careNeedQuestion.helper}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
              {c.careNeedQuestion.options.map(o => (
                <SelectCard key={o.id} icon={o.icon} label={o.label} desc={o.desc}
                  selected={data.careNeed === o.id}
                  onClick={() => onChange({ careNeed: o.id, service: null, selectedItemMeta: null, conditionFocuses: [], consultType2: null })} />
              ))}
            </div>
          </QuestionBlock>

          {/* <QuestionBlock label={`2. ${c.lifeStageQuestion.label}`} helper={c.lifeStageQuestion.helper}>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${lifeStages.length || c.lifeStageQuestion.options.length},1fr)`, gap: 10 }}>
              {(lifeStages.length ? lifeStages : c.lifeStageQuestion.options).map(o => (
                <BadgeCard key={o.id} icon={o.icon} label={o.name || o.label} desc={o.desc} badgeColor={o.badgeColor}
                  selected={data.lifeStage === o.id} onClick={() => onChange({ lifeStage: o.id, lifeStageName: o.name || o.label })} />
              ))}
            </div>
          </QuestionBlock> */}
          <QuestionBlock label={`2. ${c.lifeStageQuestion.label}`} helper={c.lifeStageQuestion.helper}>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${lifeStages.length || c.lifeStageQuestion.options.length},1fr)`, gap: 10 }}>
              {(lifeStages.length ? lifeStages : c.lifeStageQuestion.options).map(o => {
                const name = o.name || o.label;
                return (
                  <BadgeCard
                    key={o.id}
                    icon={o.icon || iconForLifeStage(name)}
                    label={name}
                    desc={o.desc}
                    badgeColor={o.badgeColor || badgeColorForLifeStage(name)}
                    selected={data.lifeStage === o.id}
                    onClick={() => onChange({ lifeStage: o.id, lifeStageName: name })}
                  />
                );
              })}
            </div>
          </QuestionBlock>
        </div>

        <FooterBar
          left={<span />}
          center={null}
          right={<BtnContinue label={c.ctaLabel} disabled={!canContinue} onClick={onNext} />}
        />
      </div>

      {/* IMAGE PLACEHOLDER — swap for your doctor + patient illustration */}
      <div style={{ width: 260, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src={c.heroImage} alt="" style={{ width: "100%", borderRadius: 16 }} onError={e => { e.currentTarget.style.display = "none"; }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 2 — Service/Program Details
// Q1 options come from useCatalogue(data.careNeed): services when careNeed is
// consultation/diagnostics/symptoms, programs when careNeed is "programs".
// Q2 (condition/symptom focuses) and Q3 (consultation modes) both come off
// whichever item the user picked in Q1 — same as before, just generalized.
// ─────────────────────────────────────────────────────────────────────────────

// export function ServiceDetailsScreen({
//   data, onChange, onNext, onBack, stepIndex, totalSteps,
//   services, servicesLoading, servicesError,
//   programs, programsLoading, programsError,
//   doctors, doctorsLoading, doctorsError,
// }) {
//   const c = content.screen2_serviceDetails;

//   const { items, loading, error } = useCatalogue(data.careNeed, {
//     services, servicesLoading, servicesError,
//     programs, programsLoading, programsError,
//     doctors, doctorsLoading, doctorsError,
//   });

//   const filteredItems = useMemo(
//     () => items.filter(i => matchesLifeStage(i, data.lifeStage)),
//     [items, data.lifeStage]
//   );

//   const selectedItem = filteredItems.find(i => i.id === data.service) || null;
//   const isDoctorFlow = selectedItem?.kind === "doctor";
//   const focusOptions = selectedItem?.focuses || [];
//   const modeOptions = selectedItem?.consultationModes || [];

//   const onSelectItem = (item) => {
//     // Services: user still picks their own condition/symptom focuses (multi-select, untouched).
//     // Doctors: speciality is fixed info, not a choice — auto-fill it so canContinue
//     // doesn't block on a non-decision, but it's rendered read-only below.
//     const autoFocus = item.kind === "doctor" ? item.focuses : [];
//     onChange({ service: item.id, selectedItemMeta: item.raw, conditionFocuses: autoFocus, consultType2: null });
//   };

//   const toggleFocus = (focus) => {
//     // Only relevant for services — doctors never call this since their focus
//     // chips aren't rendered as clickable.
//     const current = data.conditionFocuses || [];
//     const exists = current.some(f => f.id === focus.id);
//     onChange({ conditionFocuses: exists ? current.filter(f => f.id !== focus.id) : [...current, focus] });
//   };

//   const canContinue =
//     data.service &&
//     (data.conditionFocuses || []).length > 0 &&
//     (modeOptions.length === 0 || data.consultType2);

//   return (
//     <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
//       {/* left rail unchanged */}
//       <div style={{ width: 210, flexShrink: 0 }}>
//         <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
//           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//             <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: 0 }}>Your Selections</p>
//             <Icon name="editPencil" size={14} color={T.muted} />
//           </div>
//           <SummaryRow icon="target" label="Care Need" value={content.screen1_careNeed.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label || "—"} />
//           <SummaryRow icon="crosshair" label="Life Stage" value={data.lifeStageName || "—"} />
//           <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, display: "flex", alignItems: "flex-start", gap: 8 }}>
//             <Icon name="sparkle" size={14} color={T.purple700} />
//             <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.5 }}>We'll use your answers to suggest the most suitable care and options.</p>
//           </div>
//         </div>
//       </div>

//       <div style={{ flex: 1, minWidth: 0 }}>
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
//           <div>
//             <h1 style={{ fontSize: 20, fontWeight: 800, color: T.heading, margin: "0 0 4px" }}>{c.title}</h1>
//             <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>{c.subtitle}</p>
//           </div>
//           <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
//             <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.purpleLight, borderRadius: 20, padding: "5px 12px" }}>
//               <Icon name="headset" size={14} color={T.purple700} />
//               <span style={{ fontSize: 10, fontWeight: 700, color: T.purple700 }}>{c.helpBubble.label}</span>
//             </div>
//             <StepperProgress current={stepIndex} total={totalSteps} />
//           </div>
//         </div>

//         <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 18 }}>
//           <QuestionBlock label={isDoctorFlow ? "1. Which doctor would you like to consult?" : "1. Which option are you looking for?"} helper="Select one option.">
//             <InlineState loading={loading} error={error} loadingLabel="Loading available options..." errorLabel="Couldn't load options — please try again." />
//             {!loading && !error && filteredItems.length === 0 && (
//               <p style={{ fontSize: 12, color: T.muted }}>Nothing matches your selections yet — try a different life stage.</p>
//             )}
//             <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
//               {filteredItems.map(i => (
//                 <SelectCard key={i.id} icon={i.icon} label={i.label} desc={i.desc}
//                   selected={data.service === i.id} onClick={() => onSelectItem(i)} />
//               ))}
//             </div>
//           </QuestionBlock>

//           {/* Q2 — branches by kind. Services: interactive multi-select chips
//               (unchanged). Doctors: a read-only speciality card, since there's
//               nothing to choose — it's just telling the user what they picked. */}
//           {isDoctorFlow ? (
//             <QuestionBlock label="2. Speciality" helper="This doctor's area of expertise.">
//               {focusOptions.length === 0 ? (
//                 <p style={{ fontSize: 12, color: T.muted }}>No speciality listed for this doctor.</p>
//               ) : (
//                 <div style={{
//                   display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px",
//                   borderRadius: 8, border: `1.5px solid ${T.purpleBorder}`, background: T.purpleLight,
//                 }}>
//                   <Icon name="stethoscope" size={14} color={T.purple700} />
//                   <span style={{ fontSize: 12, fontWeight: 700, color: T.purple700 }}>{focusOptions[0].name}</span>
//                 </div>
//               )}
//             </QuestionBlock>
//           ) : (
//             <QuestionBlock label="2. What would you like help with?" helper="Select all that apply — based on what this covers.">
//               {!selectedItem && <p style={{ fontSize: 12, color: T.muted }}>Choose an option above first.</p>}
//               {selectedItem && focusOptions.length === 0 && (
//                 <p style={{ fontSize: 12, color: T.muted }}>No specific focuses listed — you can continue.</p>
//               )}
//               <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
//                 {focusOptions.map(f => (
//                   <SelectChip key={f.id} label={f.name} selected={(data.conditionFocuses || []).some(sel => sel.id === f.id)} onClick={() => toggleFocus(f)} />
//                 ))}
//               </div>
//             </QuestionBlock>
//           )}

//           {modeOptions.length > 0 && (
//             <QuestionBlock label="3. How would you prefer to consult?" helper="Choose your preferred consultation type.">
//               <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
//                 {modeOptions.map(m => (
//                   <SelectCard key={m.id} icon={iconForConsultMode(m.name)} label={m.name}
//                     selected={data.consultType2 === m.id} onClick={() => onChange({ consultType2: m.id, consultType2Name: m.name })} />
//                 ))}
//               </div>
//             </QuestionBlock>
//           )}

//           <QuestionBlock label={`${modeOptions.length > 0 ? "4" : "3"}. ${c.locationField.label}`} helper={c.locationField.helper}>
//             <div style={{ display: "flex", gap: 10 }}>
//               <div style={{ flex: 1, position: "relative" }}>
//                 <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
//                   <Icon name="pin" size={14} color={T.muted} />
//                 </span>
//                 <input
//                   value={data.location || ""}
//                   onChange={e => onChange({ location: e.target.value })}
//                   placeholder={c.locationField.placeholder}
//                   style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px 10px 34px", borderRadius: 8, border: `1.5px solid ${T.border}`, fontSize: 12, outline: "none" }}
//                 />
//               </div>
//               <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 14px", borderRadius: 8, border: `1.5px solid ${T.purpleBorder}`, background: "#fff", color: T.purple700, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
//                 <Icon name="crosshair" size={14} /> {c.locationField.useCurrentLocationLabel}
//               </button>
//             </div>
//           </QuestionBlock>
//         </div>

//         <FooterBar
//           left={<BtnBack onClick={onBack} />}
//           center={null}
//           right={<BtnContinue label="Continue" disabled={!canContinue} onClick={onNext} />}
//         />
//       </div>
//     </div>
//   );
// }
export function ServiceDetailsScreen({
  data, onChange, onNext, onBack, stepIndex, totalSteps,
  services, servicesLoading, servicesError,
  programs, programsLoading, programsError,
  doctors, doctorsLoading, doctorsError,
}) {
  const c = content.screen2_serviceDetails;
  const isSymptomsFlow = data.careNeed === "symptoms";
  const symptomsBranch = c.byCareNeed.symptoms;

  // Hooks must run unconditionally — for the symptoms flow this result is
  // simply unused below, no fetch cost since services/etc. are already loaded.
  const { items, loading, error } = useCatalogue(isSymptomsFlow ? null : data.careNeed, {
    services, servicesLoading, servicesError,
    programs, programsLoading, programsError,
    doctors, doctorsLoading, doctorsError,
  });

  const filteredItems = useMemo(
    () => items.filter(i => matchesLifeStage(i, data.lifeStage)),
    [items, data.lifeStage]
  );

  const selectedItem = filteredItems.find(i => i.id === data.service) || null;
  const isDoctorFlow = selectedItem?.kind === "doctor";
  const focusOptions = selectedItem?.focuses || [];
  const modeOptions = selectedItem?.consultationModes || [];

  const onSelectSymptomService = (opt) => {
    onChange({ service: opt.id, selectedItemMeta: opt, reason: null });
  };

  const onSelectItem = (item) => {
    const autoFocus = item.kind === "doctor" ? item.focuses : [];
    onChange({ service: item.id, selectedItemMeta: item.raw, conditionFocuses: autoFocus, consultType2: null });
  };

  const toggleFocus = (focus) => {
    const current = data.conditionFocuses || [];
    const exists = current.some(f => f.id === focus.id);
    onChange({ conditionFocuses: exists ? current.filter(f => f.id !== focus.id) : [...current, focus] });
  };

  const canContinue = isSymptomsFlow
    ? Boolean(data.service && data.reason && data.consultType2)
    : Boolean(
      data.service &&
      (data.conditionFocuses || []).length > 0 &&
      (modeOptions.length === 0 || data.consultType2)
    );

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
      <div style={{ width: 210, flexShrink: 0 }}>
        <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: 0 }}>Your Selections</p>
            {/* <Icon name="editPencil" size={14} color={T.muted} /> */}
          </div>
          <SummaryRow icon="target" label="Care Need" value={content.screen1_careNeed.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label || "—"} />
          <SummaryRow icon="crosshair" label="Life Stage" value={data.lifeStageName || "—"} />
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <Icon name="sparkle" size={14} color={T.purple700} />
            <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.5 }}>We'll use your answers to suggest the most suitable care and options.</p>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: T.heading, margin: "0 0 4px" }}>{c.title}</h1>
            <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>{c.subtitle}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.purpleLight, borderRadius: 20, padding: "5px 12px" }}>
              <Icon name="headset" size={14} color={T.purple700} />
              <span style={{ fontSize: 10, fontWeight: 700, color: T.purple700 }}>{c.helpBubble.label}</span>
            </div>
            <StepperProgress current={stepIndex} total={totalSteps} />
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 18 }}>
          {isSymptomsFlow ? (
            <>
              <QuestionBlock label={`1. ${symptomsBranch.serviceOptions.label}`} helper={symptomsBranch.serviceOptions.helper}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
                  {symptomsBranch.serviceOptions.options.map(o => (
                    <SelectCard key={o.id} icon={o.icon} label={o.label} desc={o.desc}
                      selected={data.service === o.id} onClick={() => onSelectSymptomService(o)} />
                  ))}
                </div>
              </QuestionBlock>

              <QuestionBlock label={`2. ${symptomsBranch.reasonOptions.label}`} helper={symptomsBranch.reasonOptions.helper}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {symptomsBranch.reasonOptions.options.map(opt => (
                    <SelectChip key={opt} label={opt} selected={data.reason === opt} onClick={() => onChange({ reason: opt })} />
                  ))}
                </div>
              </QuestionBlock>

              <QuestionBlock label={`3. ${c.consultTypeOptions.label}`} helper={c.consultTypeOptions.helper}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
                  {c.consultTypeOptions.options.map(o => (
                    <SelectCard key={o.id} icon={o.icon} label={o.label} desc={o.desc}
                      selected={data.consultType2 === o.id} onClick={() => onChange({ consultType2: o.id, consultType2Name: o.label })} />
                  ))}
                </div>
              </QuestionBlock>
            </>
          ) : (
            <>
              <QuestionBlock label={isDoctorFlow ? "1. Which doctor would you like to consult?" : "1. Which option are you looking for?"} helper="Select one option.">
                <InlineState loading={loading} error={error} loadingLabel="Loading available options..." errorLabel="Couldn't load options — please try again." />
                {!loading && !error && filteredItems.length === 0 && (
                  <p style={{ fontSize: 12, color: T.muted }}>Nothing matches your selections yet — try a different life stage.</p>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
                  {filteredItems.map(i => (
                    <SelectCard key={i.id} icon={i.icon} label={i.label} desc={i.desc}
                      selected={data.service === i.id} onClick={() => onSelectItem(i)} />
                  ))}
                </div>
              </QuestionBlock>

              {isDoctorFlow ? (
                <QuestionBlock label="2. Speciality" helper="This doctor's area of expertise.">
                  {focusOptions.length === 0 ? (
                    <p style={{ fontSize: 12, color: T.muted }}>No speciality listed for this doctor.</p>
                  ) : (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 8, border: `1.5px solid ${T.purpleBorder}`, background: T.purpleLight }}>
                      <Icon name="stethoscope" size={14} color={T.purple700} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.purple700 }}>{focusOptions[0].name}</span>
                    </div>
                  )}
                </QuestionBlock>
              ) : (
                <QuestionBlock label="2. What would you like help with?" helper="Select all that apply — based on what this covers.">
                  {!selectedItem && <p style={{ fontSize: 12, color: T.muted }}>Choose an option above first.</p>}
                  {selectedItem && focusOptions.length === 0 && (
                    <p style={{ fontSize: 12, color: T.muted }}>No specific focuses listed — you can continue.</p>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {focusOptions.map(f => (
                      <SelectChip key={f.id} label={f.name} selected={(data.conditionFocuses || []).some(sel => sel.id === f.id)} onClick={() => toggleFocus(f)} />
                    ))}
                  </div>
                </QuestionBlock>
              )}

              {modeOptions.length > 0 && (
                <QuestionBlock label="3. How would you prefer to consult?" helper="Choose your preferred consultation type.">
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
                    {modeOptions.map(m => (
                      <SelectCard key={m.id} icon={iconForConsultMode(m.name)} label={m.name}
                        selected={data.consultType2 === m.id} onClick={() => onChange({ consultType2: m.id, consultType2Name: m.name })} />
                    ))}
                  </div>
                </QuestionBlock>
              )}
            </>
          )}

          {/* <QuestionBlock label={`4. ${c.locationField.label}`} helper={c.locationField.helper}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                  <Icon name="pin" size={14} color={T.muted} />
                </span>
                <input
                  value={data.location || ""}
                  onChange={e => onChange({ location: e.target.value })}
                  placeholder={c.locationField.placeholder}
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px 10px 34px", borderRadius: 8, border: `1.5px solid ${T.border}`, fontSize: 12, outline: "none" }}
                />
              </div>
              <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 14px", borderRadius: 8, border: `1.5px solid ${T.purpleBorder}`, background: "#fff", color: T.purple700, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <Icon name="crosshair" size={14} /> {c.locationField.useCurrentLocationLabel}
              </button>
            </div>
          </QuestionBlock> */}
        </div>

        <FooterBar
          left={<BtnBack onClick={onBack} />}
          center={null}
          right={<BtnContinue label="Continue" disabled={!canContinue} onClick={onNext} />}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 3 — Health Background
// Q1 (diagnosed / condition-specific symptoms) is looked up by the condition
// key of whatever service/program the user picked on Screen 2. Everything
// else (pregnant, surgeries, medications, upload) stays generic — these are
// the "extra questions" that apply no matter which condition is in focus.
// ─────────────────────────────────────────────────────────────────────────────
export function HealthBackgroundScreen({ data, onChange, onNext, onBack, stepIndex, totalSteps }) {
  const c = content.screen3_healthBackground;

  const conditionKey = getConditionKey(data.selectedItemMeta);
  const byCondition = c.diagnosedQuestion.byCondition || {};
  // const diagnosedConfig = c.diagnosedQuestion.byCondition[conditionKey] || c.diagnosedQuestion.byCondition.default;
  const diagnosedConfig =
    byCondition[conditionKey] ||
    byCondition.default || {
      label: c.diagnosedQuestion.label,
      helper: c.diagnosedQuestion.helper,
      options: c.diagnosedQuestion.options,
      exclusiveOption: c.diagnosedQuestion.exclusiveOption,
    };

  const toggleMulti = (field, value, exclusiveOption) => {
    const current = data[field] || [];
    if (value === exclusiveOption) { onChange({ [field]: [exclusiveOption] }); return; }
    const without = current.filter(v => v !== exclusiveOption);
    onChange({ [field]: without.includes(value) ? without.filter(v => v !== value) : [...without, value] });
  };

  const canContinue = (data.diagnosed || []).length > 0 && data.pregnant && (data.surgeries || []).length > 0 && data.medications;

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ maxWidth: 540 }}>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: T.heading, margin: "0 0 4px" }}>{c.title}</h1>
            <p style={{ fontSize: 12, color: T.sub, margin: 0, lineHeight: 1.5 }}>{c.subtitle}</p>
          </div>
          <StepperProgress current={stepIndex} total={totalSteps} />
        </div>

        <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 18 }}>
          {/* Condition-focused question — content changes per selected service/program */}
          <QuestionBlock label={`1. ${diagnosedConfig.label}`} helper={diagnosedConfig.helper}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
              {diagnosedConfig.options.map(o => (
                <IconChip key={o.id} icon={o.icon} label={o.label}
                  selected={(data.diagnosed || []).includes(o.label)}
                  onClick={() => toggleMulti("diagnosed", o.label, diagnosedConfig.exclusiveOption)} />
              ))}
            </div>
          </QuestionBlock>

          {/* Generic "extra questions" — same regardless of condition */}
          <QuestionBlock label={`2. ${c.pregnantQuestion.label}`}>
            <div style={{ display: "flex", gap: 10 }}>
              {c.pregnantQuestion.options.map(o => (
                <SelectChip key={o.id} label={o.label} selected={data.pregnant === o.id} onClick={() => onChange({ pregnant: o.id })} />
              ))}
            </div>
          </QuestionBlock>

          <QuestionBlock label={`3. ${c.surgeryQuestion.label}`} helper={c.surgeryQuestion.helper}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
              {c.surgeryQuestion.options.map(o => (
                <IconChip key={o.id} icon={o.icon} label={o.label}
                  selected={(data.surgeries || []).includes(o.label)}
                  onClick={() => toggleMulti("surgeries", o.label, c.surgeryQuestion.exclusiveOption)} />
              ))}
            </div>
          </QuestionBlock>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <QuestionBlock label={`4. ${c.medicationsQuestion.label}`}>
              <div style={{ display: "flex", gap: 10, marginBottom: data.medications === "yes" ? 10 : 0 }}>
                {["yes", "no"].map(v => (
                  <SelectChip key={v} label={v === "yes" ? "Yes" : "No"} selected={data.medications === v} onClick={() => onChange({ medications: v })} />
                ))}
              </div>
              {data.medications === "yes" && (
                <textarea
                  value={data.medicationList || ""}
                  maxLength={c.medicationsQuestion.followUp.maxLength}
                  onChange={e => onChange({ medicationList: e.target.value })}
                  placeholder={c.medicationsQuestion.followUp.placeholder}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 60, borderRadius: 8, border: `1.5px solid ${T.border}`, padding: 10, fontSize: 11, resize: "vertical", fontFamily: "inherit" }}
                />
              )}
            </QuestionBlock>

            {/* <QuestionBlock label={`5. ${c.reportsUpload.label}`} helper={c.reportsUpload.helper}>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: 8, border: `1.5px dashed ${T.purpleBorder}`, background: T.purpleLight, color: T.purple700, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  <Icon name="upload" size={14} /> {c.reportsUpload.uploadLabel}
                </button>
                <button style={{ padding: "10px 14px", borderRadius: 8, border: `1.5px solid ${T.border}`, background: "#fff", color: T.sub, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  {c.reportsUpload.skipLabel}
                </button>
              </div>
              <p style={{ fontSize: 9, color: T.muted, margin: "6px 0 0" }}>{c.reportsUpload.hint}</p>
            </QuestionBlock> */}
          </div>
        </div>

        <div style={{ background: T.lavenderBg, borderRadius: 12, padding: "10px 16px", marginTop: 14, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <Icon name="editPencil" size={14} color={T.purple700} />
          <span style={{ fontSize: 11, fontWeight: 700, color: T.heading }}>Your Selections</span>
          <SummaryRow icon="target" label="Care Need" value={content.screen1_careNeed.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label || "—"} />
          <SummaryRow icon="crosshair" label="Life Stage" value={data.lifeStageName || "—"} />
          <SummaryRow icon="video" label="Visit Preference" value={data.consultType2Name || "—"} />
        </div>

        <FooterBar
          left={<BtnBack onClick={onBack} />}
          center={null}
          right={<BtnContinue label="See My Recommendations" disabled={!canContinue} onClick={onNext} />}
        />
      </div>

      <div style={{ width: 220, flexShrink: 0 }}>
        <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <img src={c.heroImage} alt="" style={{ width: "100%", borderRadius: 10 }} onError={e => { e.currentTarget.style.display = "none"; }} />
          <p style={{ fontWeight: 800, fontSize: 13, color: T.heading, margin: 0 }}>{c.whyWeAsk.title}</p>
          <p style={{ fontSize: 11, color: T.sub, margin: 0, lineHeight: 1.5 }}>{c.whyWeAsk.intro}</p>
          {c.whyWeAsk.points.map(p => (
            <div key={p.title} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={p.icon} size={13} color={T.purple700} />
              </div>
              <div>
                <p style={{ fontWeight: 700, fontSize: 11, color: T.heading, margin: "0 0 2px" }}>{p.title}</p>
                <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 4 — Recommendations
// Ranks the SAME catalogue (services or programs) used on Screen 2 against
// everything picked so far, then shows the winning item's own detail fields
// (whatItIs / whenToSee / howEvaluated / treatmentOptions / selfCareTips, if
// present) as the "why recommended" content — no extra fetch needed.
// ─────────────────────────────────────────────────────────────────────────────
// export function RecommendationsScreen({
//   data, onBack, stepIndex, totalSteps,
//   services, servicesLoading, servicesError,
//   programs, programsLoading, programsError,
//   doctors, doctorsLoading, doctorsError, 
//   onBookService,
// }) {
//   const c = content.screen4_recommendations;
//   const s1 = content.screen1_careNeed;

//   const { items, loading, error } = useCatalogue(data.careNeed, {
//     services, servicesLoading, servicesError,
//     programs, programsLoading, programsError,doctors, doctorsLoading, doctorsError, 
//   });

//   const ranked = useMemo(
//     () => items.map(i => ({ item: i, score: scoreItem(i, data) })).sort((a, b) => b.score - a.score),
//     [items, data]
//   );

//   const best = ranked[0]?.item || items.find(i => i.id === data.service) || null;
//   const others = ranked.filter(r => r.item.id !== best?.id).slice(0, 3).map(r => r.item);
//   const detail = best?.raw?.detail || {};

//   const bookItem = (item) => {
//     if (onBookService) return onBookService(item.raw);
//     window.location.href = `/book/${item.raw.serviceCode || item.id}?serviceId=${item.id}`;
//   };

//   if (loading) return <p style={{ fontSize: 13, color: T.muted, padding: 24 }}>Finding your best match...</p>;
//   if (error || !best) return <p style={{ fontSize: 13, color: T.red, padding: 24 }}>We couldn't load recommendations — please go back and try again.</p>;

//   return (
//     <div>
//       <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
//         <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", color: T.body, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>
//           <Icon name="chevronLeft" size={14} /> Back
//         </button>
//         <StepperProgress current={stepIndex} total={totalSteps} />
//       </div>

//       <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
//         <div style={{ width: 210, flexShrink: 0 }}>
//           <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
//             <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//               <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: 0 }}>{c.summaryTitle}</p>
//               <Icon name="editPencil" size={14} color={T.muted} />
//             </div>
//             <SummaryRow icon="target" label="Care Need" value={s1.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label || "—"} />
//             <SummaryRow icon="doctorChat" label="Selected" value={items.find(i => i.id === data.service)?.label || "—"} />
//             <SummaryRow icon="crosshair" label="Life Stage" value={data.lifeStageName || "—"} />
//             <SummaryRow icon="video" label="Consultation Type" value={data.consultType2Name || "—"} />
//             <SummaryRow icon="pin" label="Location" value={data.location || "—"} />
//           </div>
//         </div>

//         <div style={{ flex: 1, minWidth: 0 }}>
//           <h1 style={{ fontSize: 20, fontWeight: 800, color: T.heading, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
//             {c.title} <Icon name="sparkle" size={16} color={T.purple700} />
//           </h1>
//           <p style={{ fontSize: 12, color: T.sub, margin: "0 0 14px" }}>{c.subtitle}</p>

//           <div style={{ background: "#fff", borderRadius: 16, border: `2px solid ${T.purpleBorder}`, overflow: "hidden" }}>
//             <div style={{ display: "flex", gap: 16, padding: 18 }}>
//               <img src={c.bestMatchImage} alt="" style={{ width: 170, height: 130, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: T.lavenderBg }} onError={e => { e.currentTarget.style.display = "none"; }} />
//               <div style={{ flex: 1 }}>
//                 <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: T.purple700, color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, marginBottom: 8 }}>
//                   <Icon name="sparkle" size={10} color="#fff" /> {c.bestMatchBadge}
//                 </span>
//                 <p style={{ fontWeight: 800, fontSize: 15, color: T.heading, margin: "0 0 4px" }}>{best.label}</p>
//                 <p style={{ fontSize: 11, color: T.sub, margin: "0 0 10px", lineHeight: 1.5 }}>{detail.whatItIs || best.desc}</p>
//                 <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
//                   {[
//                     (data.conditionFocuses || []).length > 0 ? "Matches the symptoms/concerns you selected" : "Suitable for your selected concern",
//                     "Available in your preferred location",
//                     "Experienced women's health experts",
//                   ].map(f => (
//                     <div key={f} style={{ display: "flex", alignItems: "center", gap: 6 }}>
//                       <Icon name="check" size={11} color={T.green} />
//                       <span style={{ fontSize: 10, color: T.body }}>{f}</span>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>
//             <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderTop: `1px solid ${T.border}`, background: T.lavenderBg }}>
//               <div style={{ display: "flex", gap: 18 }}>
//                 <SummaryRow icon="clinic" label="Type" value={best.consultationModes?.[0]?.name || "Program enrollment"} />
//                 <SummaryRow icon="crosshair" label="Duration" value={best.duration ? `${best.duration} mins` : "—"} />
//               </div>
//               <button onClick={() => bookItem(best)} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: T.purpleBtn, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Book Now →</button>
//             </div>
//           </div>

//           {/* Condition-focused reasoning — pulls straight off the winning item's
//               own detail fields (whenToSee, howEvaluated, treatmentOptions,
//               selfCareTips) when the API supplies them; no extra fetch. */}
//           <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, marginTop: 14, display: "flex", gap: 14, alignItems: "flex-start" }}>
//             <img src={c.reasonBlockImage} alt="" style={{ width: 60, flexShrink: 0 }} onError={e => { e.currentTarget.style.display = "none"; }} />
//             <div>
//               <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: "0 0 8px" }}>{c.reasonBlockTitle}</p>
//               {[
//                 `You selected ${s1.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label} as your concern`,
//                 data.lifeStageName ? `Your life stage (${data.lifeStageName}) matches this care` : null,
//                 data.location ? `Available near ${data.location}` : "Available in your area",
//                 (data.conditionFocuses || []).length > 0
//                   ? `Matches what you selected: ${(data.conditionFocuses || []).map(f => f.name).join(", ")}`
//                   : null,
//                 detail.whenToSee ? `When to see a doctor: ${detail.whenToSee}` : null,
//                 detail.treatmentOptions ? `Typical treatment: ${detail.treatmentOptions}` : null,
//                 detail.selfCareTips ? `Self-care: ${detail.selfCareTips}` : null,
//               ].filter(Boolean).map(r => (
//                 <div key={r} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
//                   <Icon name="check" size={11} color={T.purple700} style={{ marginTop: 2, flexShrink: 0 }} />
//                   <span style={{ fontSize: 11, color: T.body, lineHeight: 1.4 }}>{r}</span>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>

//         <div style={{ width: 230, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14 }}>
//           <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 14 }}>
//             <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: "0 0 10px" }}>{c.otherOptionsTitle}</p>
//             {others.map(o => (
//               <div key={o.id} onClick={() => bookItem(o)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
//                 <div style={{ width: 34, height: 34, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
//                   <Icon name={o.icon} size={16} color={T.purple700} />
//                 </div>
//                 <div style={{ flex: 1, minWidth: 0 }}>
//                   <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, margin: 0 }}>{o.label}</p>
//                   <p style={{ fontSize: 9, color: T.muted, margin: 0 }}>{o.duration ? `${o.duration} mins · ` : ""}{o.consultationModes?.[0]?.name || "—"}</p>
//                 </div>
//                 <Icon name="chevronRight" size={13} color={T.muted} />
//               </div>
//             ))}
//             {others.length === 0 && <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>No other close matches right now.</p>}
//           </div>

//           <div style={{ background: "linear-gradient(135deg,#EDE9FB,#FFF0F5)", borderRadius: 14, padding: 14 }}>
//             <img src={c.programImage} alt="" style={{ width: "100%", height: 70, objectFit: "cover", borderRadius: 8, marginBottom: 8 }} onError={e => { e.currentTarget.style.display = "none"; }} />
//             <p style={{ fontWeight: 700, fontSize: 11, color: T.heading, margin: "0 0 4px" }}>{c.programsTitle}</p>
//             <p style={{ fontSize: 10, color: T.sub, margin: "0 0 8px", lineHeight: 1.4 }}>Guided, ongoing support built around what you told us.</p>
//             <button
//               onClick={() => {
//                 const program = (programs || [])[0];
//                 if (program) bookItem(normalizeProgram(program));
//               }}
//               style={{ fontSize: 11, fontWeight: 700, color: T.purple700, background: "none", border: "none", cursor: "pointer", padding: 0 }}
//             >
//               Explore Program →
//             </button>
//           </div>
//         </div>
//       </div>

//       <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: T.lavenderBg, borderRadius: 14, padding: "12px 18px", marginTop: 18 }}>
//         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
//           <img src={c.footerBar.avatarImage} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", background: "#fff" }} onError={e => { e.currentTarget.style.display = "none"; }} />
//           <div>
//             <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, margin: 0 }}>{c.footerBar.unsureTitle}</p>
//             <p style={{ fontSize: 10, color: T.sub, margin: 0 }}>{c.footerBar.unsureSubtitle}</p>
//           </div>
//         </div>
//         <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, border: `1.5px solid ${T.purpleBorder}`, background: "#fff", color: T.purple700, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
//           <Icon name="headset" size={14} /> {c.footerBar.talkToExpertLabel}
//         </button>
//         <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
//           <Icon name="lock" size={13} color={T.muted} />
//           <span style={{ fontSize: 10, color: T.muted }}>{c.footerBar.privacyLabel}</span>
//         </div>
//       </div>
//     </div>
//   );
// }
export function RecommendationsScreen({
  data, onBack, stepIndex, totalSteps,
  services, servicesLoading, servicesError,
  programs, programsLoading, programsError,
  doctors, doctorsLoading, doctorsError,
  onBookService,
}) {
  const c = content.screen4_recommendations;
  const s1 = content.screen1_careNeed;
  const isSymptomsFlow = data.careNeed === "symptoms";
  const symptomsBranch = content.screen2_serviceDetails.byCareNeed.symptoms;
  const navigate = useNavigate();

  const { items: catalogueItems, loading: catalogueLoading, error: catalogueError } = useCatalogue(isSymptomsFlow ? null : data.careNeed, {
    services, servicesLoading, servicesError,
    programs, programsLoading, programsError,
    doctors, doctorsLoading, doctorsError,
  });

  const items = isSymptomsFlow
    ? symptomsBranch.serviceOptions.options.map(o => normalizeSymptomOption(o, data.reason))
    : catalogueItems;
  const loading = isSymptomsFlow ? false : catalogueLoading;
  const error = isSymptomsFlow ? null : catalogueError;

  const ranked = useMemo(
    () => items
      .map(i => ({ item: i, score: isSymptomsFlow ? (i.id === data.service ? 999 : 0) : scoreItem(i, data) }))
      .sort((a, b) => b.score - a.score),
    [items, data, isSymptomsFlow]
  );

  const best = ranked[0]?.item || items.find(i => i.id === data.service) || null;
  const others = ranked.filter(r => r.item.id !== best?.id).slice(0, 3).map(r => r.item);
  const detail = best?.raw?.detail || {};

  // const bookItem = (item) => {
  //   if (onBookService) return onBookService(item.raw);
  //   window.location.href = `/book/${item.raw.serviceCode || item.id}?serviceId=${item.id}`;
  // };

  const bookItem = (item) => {
    // Let the parent app override booking entirely if it wants to (e.g. to
    // fire analytics first) — falls through to route-based navigation below.
    if (onBookService) return onBookService(item.raw, item.kind);

    switch (item.kind) {
      case "doctor": {
        const providerId = item.raw.providerId || item.raw.id;
        navigate(`/book-appointment?openModal=true&doctorId=${providerId}`);
        return;
      }
      case "program": {
        navigate(`/programs`);
        return;
      }
      case "service":
      case "symptom":
      default: {
        // Service/symptom bookings route through speciality-based booking.
        // Adjust this fallback chain once you confirm the real field name
        // your services API uses for speciality — right now it tries the
        // most likely candidates in order.
        const specialityId =
          item.raw.specialityId ||
          item.raw.speciality?.id ||
          item.raw.categoryId ||
          item.raw.id;
        navigate(`/book-appointment?specialityId=${specialityId}`);
        return;
      }
    }
  };
  if (loading) return <p style={{ fontSize: 13, color: T.muted, padding: 24 }}>Finding your best match...</p>;
  if (error || !best) return <p style={{ fontSize: 13, color: T.red, padding: 24 }}>We couldn't load recommendations — please go back and try again.</p>;

  return (
    <div>
      {/* header unchanged */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", color: T.body, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>
          <Icon name="chevronLeft" size={14} /> Back
        </button>
        <StepperProgress current={stepIndex} total={totalSteps} />
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        <div style={{ width: 210, flexShrink: 0 }}>
          <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: 0 }}>{c.summaryTitle}</p>
              {/* <Icon name="editPencil" size={14} color={T.muted} /> */}
            </div>
            <SummaryRow icon="target" label="Care Need" value={s1.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label || "—"} />
            <SummaryRow icon="doctorChat" label="Selected" value={items.find(i => i.id === data.service)?.label || "—"} />
            <SummaryRow icon="crosshair" label="Life Stage" value={data.lifeStageName || "—"} />
            <SummaryRow icon="video" label="Consultation Type" value={data.consultType2Name || "—"} />
            <SummaryRow icon="pin" label="Location" value={data.location || "—"} />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: T.heading, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
            {c.title} <Icon name="sparkle" size={16} color={T.purple700} />
          </h1>
          <p style={{ fontSize: 12, color: T.sub, margin: "0 0 14px" }}>{c.subtitle}</p>

          <div style={{ background: "#fff", borderRadius: 16, border: `2px solid ${T.purpleBorder}`, overflow: "hidden" }}>
            <div style={{ display: "flex", gap: 16, padding: 18 }}>
              <img src={c.bestMatchImage} alt="" style={{ width: 170, height: 130, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: T.lavenderBg }} onError={e => { e.currentTarget.style.display = "none"; }} />
              <div style={{ flex: 1 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: T.purple700, color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, marginBottom: 8 }}>
                  <Icon name="sparkle" size={10} color="#fff" /> {c.bestMatchBadge}
                </span>
                <p style={{ fontWeight: 800, fontSize: 15, color: T.heading, margin: "0 0 4px" }}>{best.label}</p>
                <p style={{ fontSize: 11, color: T.sub, margin: "0 0 10px", lineHeight: 1.5 }}>{detail.whatItIs || best.desc}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    (data.conditionFocuses || []).length > 0 ? "Matches the symptoms/concerns you selected" : "Suitable for your selected concern",
                    "Available in your preferred location",
                    "Experienced women's health experts",
                  ].map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Icon name="check" size={11} color={T.green} />
                      <span style={{ fontSize: 10, color: T.body }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderTop: `1px solid ${T.border}`, background: T.lavenderBg }}>
              <div style={{ display: "flex", gap: 18 }}>
                <SummaryRow icon="clinic" label="Type" value={best.consultationModes?.[0]?.name || data.consultType2Name || "Program enrollment"} />
                <SummaryRow icon="crosshair" label="Duration" value={best.duration ? `${best.duration} mins` : "—"} />
              </div>
              <button onClick={() => bookItem(best)} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: T.purpleBtn, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Book Now →</button>
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 16, marginTop: 14, display: "flex", gap: 14, alignItems: "flex-start" }}>
            <img src={c.reasonBlockImage} alt="" style={{ width: 60, flexShrink: 0 }} onError={e => { e.currentTarget.style.display = "none"; }} />
            <div>
              <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: "0 0 8px" }}>{c.reasonBlockTitle}</p>
              {[
                `You selected ${s1.careNeedQuestion.options.find(o => o.id === data.careNeed)?.label} as your concern`,
                data.lifeStageName ? `Your life stage (${data.lifeStageName}) matches this care` : null,
                data.location ? `Available near ${data.location}` : "Available in your area",
                isSymptomsFlow && data.reason ? `Matches your reason: ${data.reason}` : null,
                !isSymptomsFlow && (data.conditionFocuses || []).length > 0
                  ? `Matches what you selected: ${(data.conditionFocuses || []).map(f => f.name).join(", ")}`
                  : null,
                detail.whenToSee ? `When to see a doctor: ${detail.whenToSee}` : null,
                detail.treatmentOptions ? `Typical treatment: ${detail.treatmentOptions}` : null,
                detail.selfCareTips ? `Self-care: ${detail.selfCareTips}` : null,
              ].filter(Boolean).map(r => (
                <div key={r} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
                  <Icon name="check" size={11} color={T.purple700} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: T.body, lineHeight: 1.4 }}>{r}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ width: 230, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`, padding: 14 }}>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: "0 0 10px" }}>{c.otherOptionsTitle}</p>
            {others.map(o => (
              <div key={o.id} onClick={() => bookItem(o)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={o.icon} size={16} color={T.purple700} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, margin: 0 }}>{o.label}</p>
                  <p style={{ fontSize: 9, color: T.muted, margin: 0 }}>{o.duration ? `${o.duration} mins · ` : ""}{o.consultationModes?.[0]?.name || data.consultType2Name || "—"}</p>
                </div>
                <Icon name="chevronRight" size={13} color={T.muted} />
              </div>
            ))}
            {others.length === 0 && <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>No other close matches right now.</p>}
          </div>

          <div style={{ background: "linear-gradient(135deg,#EDE9FB,#FFF0F5)", borderRadius: 14, padding: 14 }}>
            <img src={c.programImage} alt="" style={{ width: "100%", height: 70, objectFit: "cover", borderRadius: 8, marginBottom: 8 }} onError={e => { e.currentTarget.style.display = "none"; }} />
            <p style={{ fontWeight: 700, fontSize: 11, color: T.heading, margin: "0 0 4px" }}>{c.programsTitle}</p>
            <p style={{ fontSize: 10, color: T.sub, margin: "0 0 8px", lineHeight: 1.4 }}>Guided, ongoing support built around what you told us.</p>
            <button
              onClick={() => {
                const program = (programs || [])[0];
                if (program) bookItem(normalizeProgram(program));
              }}
              style={{ fontSize: 11, fontWeight: 700, color: T.purple700, background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              Explore Program →
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: T.lavenderBg, borderRadius: 14, padding: "12px 18px", marginTop: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={c.footerBar.avatarImage} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", background: "#fff" }} onError={e => { e.currentTarget.style.display = "none"; }} />
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, margin: 0 }}>{c.footerBar.unsureTitle}</p>
            <p style={{ fontSize: 10, color: T.sub, margin: 0 }}>{c.footerBar.unsureSubtitle}</p>
          </div>
        </div>
        {/* <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, border: `1.5px solid ${T.purpleBorder}`, background: "#fff", color: T.purple700, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          <Icon name="headset" size={14} /> {c.footerBar.talkToExpertLabel}
        </button> */}
        <TalkToCareGuideButton
  label={c.footerBar.talkToExpertLabel}
  style={{
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    borderRadius: 20,
    border: `1.5px solid ${T.purpleBorder}`,
    background: "#fff",
    color: T.purple700,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  }}
/>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="lock" size={13} color={T.muted} />
          <span style={{ fontSize: 10, color: T.muted }}>{c.footerBar.privacyLabel}</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT — fetches services & programs ONCE, wires all 4 screens together
// ─────────────────────────────────────────────────────────────────────────────
const STEP_KEYS = content.steps.map(s => s.key);

export default function FindCareJourney({ onBookService }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});

  const { services, loading: servicesLoading, error: servicesError } = useServices();
  const { programs, lifeStages, loading: programsLoading, error: programsError } = usePrograms();
  const { doctors, loading: doctorsLoading, error: doctorsError } = useDoctors();
  const onChange = patch => setData(prev => ({ ...prev, ...patch }));
  const next = () => setStep(s => Math.min(s + 1, STEP_KEYS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const screenProps = {
    data, onChange, onNext: next, onBack: back,
    stepIndex: step, totalSteps: STEP_KEYS.length,
    services, servicesLoading, servicesError,
    programs, programsLoading, programsError,
    doctors, doctorsLoading, doctorsError,
    lifeStages,
    onBookService,
  };

  const SCREENS = {
    careNeed: <CareNeedScreen {...screenProps} />,
    serviceDetails: <ServiceDetailsScreen {...screenProps} />,
    healthBackground: <HealthBackgroundScreen {...screenProps} />,
    recommendations: <RecommendationsScreen {...screenProps} />,
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#e8f4f8 0%,#f0eeff 50%,#e8f4f8 100%)", padding: "32px 16px", boxSizing: "border-box" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", background: "#fff", borderRadius: 20, padding: "24px 32px", boxShadow: "0 4px 40px rgba(61,26,138,0.09)", border: `1px solid ${T.border}` }}>
        {SCREENS[STEP_KEYS[step]]}
      </div>
    </div>
  );
}